import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { DESIGN_TOKENS, COMPONENTS, MOTION, utils } from '../design-system';

// Arrow icon for buttons

const ArrowIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const Hero: React.FC = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 300], [0, 100]);
  const opacity = useTransform(scrollY, [0, 200], [1, 0]);

  const handleLaunchApp = () => {
    window.location.href = '/app';
  };

  return (
    <section className="relative pt-16 pb-8 md:py-12 lg:py-16 overflow-hidden">
      {/* Optimized Background - Matching other sections */}
      <div className="absolute inset-0">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-100 via-gray-50 to-slate-200"></div>
        
        {/* Subtle geometric elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-1/4 w-64 h-64 bg-gradient-to-br from-blue-200/30 to-transparent rounded-full animate-float"></div>
          <div className="absolute bottom-20 right-1/4 w-48 h-48 bg-gradient-to-tl from-slate-200/30 to-transparent rounded-full animate-pulse-slow"></div>
        </div>
      </div>

      {/* Main Content - Following other sections' pattern */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 xl:px-16 relative z-10">
        
        {/* Glass container matching Stats/HowItWorks sections */}
        <div className="relative backdrop-blur-sm bg-white/20 rounded-3xl border border-white/30 shadow-2xl shadow-black/5 p-6 sm:p-8 md:p-12 lg:p-16">
          {/* Inner glow effect */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/10 via-transparent to-white/5 pointer-events-none"></div>
          
          <div className="relative z-10 text-center max-w-5xl mx-auto">
            {/* Badge */}
            <motion.div 
              className="inline-flex items-center px-4 py-2 rounded-full bg-ripple-blue/10 backdrop-blur-md border border-ripple-blue/30 shadow-lg shadow-ripple-blue/10 mb-8 sm:mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(59, 130, 246, 0.2)" }}
            >
              <span className="text-sm font-medium text-ripple-blue tracking-wide">
                RIPPLEONE V1 IS LIVE
              </span>
            </motion.div>

            {/* Main Headlines - Responsive spacing */}
            <motion.div 
              className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 pb-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 drop-shadow-sm">
                The First XRP Neobank
              </h1>
              
              <div className="relative">
                <motion.h2 
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-tight bg-gradient-to-r from-ripple-blue via-ripple-blue-light to-ripple-teal bg-clip-text text-transparent drop-shadow-sm overflow-visible relative z-10"
                  style={{ 
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    backgroundSize: '300% 100%',
                    lineHeight: '1.1',
                    paddingBottom: '0.1em'
                  }}
                  animate={{
                    backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
                  }}
                  transition={{
                    duration: 8,
                    ease: "easeInOut",
                    repeat: Infinity,
                    repeatType: "loop"
                  }}
                >
                  Built for Real-World Utility
                </motion.h2>
                
                {/* Glass shimmer overlay */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 opacity-0"
                  animate={{
                    x: ['-200%', '200%'],
                    opacity: [0, 0.6, 0]
                  }}
                  transition={{
                    duration: 3,
                    ease: "easeInOut",
                    repeat: Infinity,
                    repeatDelay: 2
                  }}
                />
              </div>
            </motion.div>

            {/* Description */}
            <motion.div 
              className="max-w-2xl mx-auto mb-6 sm:mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <p className="text-base sm:text-lg md:text-xl leading-relaxed text-gray-700 font-medium px-2 sm:px-0">
                Turn idle XRP into yield-generating capital. Swap, earn, and spend with speed and security — powered by the XRP Ledger.
              </p>
            </motion.div>

            {/* Call-to-Action Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-10"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
            >
              <motion.button
                onClick={handleLaunchApp}
                className="px-8 py-3 sm:px-10 sm:py-4 bg-gradient-to-r from-ripple-blue to-ripple-blue-light text-white font-semibold rounded-2xl hover:shadow-xl hover:shadow-ripple-blue/30 transition-all duration-300 inline-flex items-center justify-center space-x-2 backdrop-blur-sm w-full sm:w-auto"
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>Launch App</span>
                <ArrowIcon className="w-4 h-4" />
              </motion.button>
              
              <motion.button
                className="px-8 py-3 sm:px-10 sm:py-4 bg-white/60 hover:bg-white/80 backdrop-blur-md border border-white/40 hover:border-white/60 text-gray-700 font-semibold rounded-2xl transition-all duration-300 shadow-lg shadow-black/5 w-full sm:w-auto"
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
              >
                View Docs
              </motion.button>
            </motion.div>


          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero; 